import React, { useState, useEffect, useCallback } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { handleApiCall } from '../../apis';
import DateRangeFilter from '../../Components/DateRangeFilter/DateRangeFilter';
import { appendDateRangeParams, DATE_PARAM_KEYS, toApiDate } from '../../utils/dateRange';
import { endpoint } from '../../utils/endpoints';
import Toastify, { notifyError, notifySuccess } from '../../utils/tostfy';
import LeftAngle from '../../assets/image/left-angle.svg'
import RightAngle from '../../assets/image/right-angle.svg'
import EditIcon from '../../assets/image/edit-icon.svg'
import DownloadIcon from '../../assets/image/download.svg'
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../utils/search";
import CsvStatusBadge from '../../Components/CsvStatusBadge/CsvStatusBadge';
import { useAdminCsvReady } from '../../hooks/useAdminCsvReady';
import {
    isFuelSettlementCsvType,
    patchSalaryCsvLogRow,
    resolveCsvStatusLabel,
} from '../../utils/adminCsvSocket';
import { formatDateTime } from '../../utils/formatDateTime';
import { formatInr as formatMoney } from '../../utils/formatAmount';
import PermissionGate from '../../Components/PermissionGate/PermissionGate';
import { MODULES } from '../../utils/permissions';
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const formatDisplayDate = (value) => formatDateTime(value, '-');

const escapeCsvCell = (value) => {
    const s = value == null ? '' : String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
};

const downloadCsvFile = (csvRows, filename) => {
    const header = [
        'S.No.',
        'Date',
        'Vehicle Number',
        'Fuel Quantity',
        'Fuel Price',
        'Fuel Operator Name',
        'Fuel Amount',
        'Swap Amount By Card',
        'Cash Given By',
        'Cash',
        'TDS',
        'Remaining Balance',
    ];
    const lines = [
        header.map(escapeCsvCell).join(','),
        ...csvRows.map((row, index) =>
            [
                index + 1,
                row.date,
                row.vehicleNumber,
                row.fuelQuantity,
                row.fuelPrice,
                row.fuelOperatorName,
                row.fuelAmount,
                row.swapAmountByCard,
                row.cashGivenBy,
                row.cash,
                row.tds,
                row.remainingBalance,
            ]
                .map(escapeCsvCell)
                .join(',')
        ),
    ];
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

const ViewTabButton = ({ active, label, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={`h-10 md:min-w-[7.5rem] rounded-md border px-6 text-sm font-UbuntuMedium transition-colors ${
            active
                ? 'border-[#000053] bg-[#000053] text-white'
                : 'border-[#000053] bg-white text-[#000053] hover:bg-[#000053]/5'
        }`}
    >
        {label}
    </button>
);

function formatLogDate(value) {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

function formatLogFilters(filter) {
    if (filter == null || filter === '') return '—';
    if (typeof filter === 'string') return filter.trim() || '—';
    if (typeof filter !== 'object') return '—';

    const parts = [];
    if (filter.fuel_pump_name) parts.push(`Fuel Pump: ${filter.fuel_pump_name}`);
    if (filter.mipl_location) parts.push(`MIPL Location: ${filter.mipl_location}`);
    if (filter.from_date && filter.to_date) {
        parts.push(`From ${formatLogDate(filter.from_date)} to ${formatLogDate(filter.to_date)}`);
    } else if (filter.from_date) {
        parts.push(`From ${formatLogDate(filter.from_date)}`);
    } else if (filter.to_date) {
        parts.push(`To ${formatLogDate(filter.to_date)}`);
    }
    if (filter.search) parts.push(`Search: ${filter.search}`);
    return parts.length ? parts.join(', ') : '—';
}

const mapFuelSettlementCsvLogRow = (item, index) => {
    const link = item?.csv_link || item?.file_url || item?.download_url || '';
    const fileName =
        item?.file_name ||
        item?.filename ||
        (link ? decodeURIComponent(String(link).split('/').pop()?.split('?')[0] || '') : '') ||
        '—';

    return {
        id: item?._id || item?.id || item?.csv_log_id || `fuel-settlement-log-${index}`,
        fileName,
        filters: formatLogFilters(item?.filter ?? item?.filters),
        createdAt: formatDateTime(item?.createdAt || item?.created_at),
        status: resolveCsvStatusLabel(item?.status, link),
        downloadUrl: link,
    };
};

const extractCsvLogList = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.csv_log_data)) return data.csv_log_data;
    if (Array.isArray(data?.fuel_settlement_csv_log)) return data.fuel_settlement_csv_log;
    if (Array.isArray(data?.fuel_settlement_log)) return data.fuel_settlement_log;
    return [];
};

const resolveVehicleId = (vehicleId) => {
    if (vehicleId == null || vehicleId === '') return '';
    if (typeof vehicleId === 'object') {
        const id = vehicleId._id ?? vehicleId.id ?? '';
        return id ? String(id).trim() : '';
    }
    return String(vehicleId).trim();
};

const mapSettlementDetailRow = (item) => {
    const veh = item?.vehicle_id;
    const vehicleId = resolveVehicleId(veh);
    const vehicleNumber =
        (typeof veh === 'object' && (veh?.name || veh?.registration_number)) ||
        item?.registration_number ||
        '-';
    const qty = item?.fuel_quantity;
    return {
        id: item?._id || item?.id || '',
        vehicleId,
        date: formatDisplayDate(item?.createdAt || item?.created_at),
        vehicleNumber,
        fuelQuantity:
            qty != null && qty !== '' && !Number.isNaN(Number(qty))
                ? `${Number(qty).toLocaleString('en-IN')} L`
                : '-',
        fuelPrice: formatMoney(item?.fuel_rate),
        fuelOperatorName: item?.fuel_operator_full_name || item?.fuel_operator_name || '-',
        fuelAmount: formatMoney(item?.fuel_amount),
        swapAmountByCard: formatMoney(item?.swap_amount_by_card),
        cashGivenBy: item?.cash_given_by || '-',
        cash: formatMoney(item?.cash_given_amount),
        tds: formatMoney(item?.tds_amount),
        tdsAmountRaw:
            item?.tds_amount != null && item?.tds_amount !== ''
                ? Number(item.tds_amount)
                : 0,
        remainingBalance: formatMoney(item?.remaining_balance),
        settlementAmountRaw:
            item?.settlement_amount != null && item?.settlement_amount !== ''
                ? Number(item.settlement_amount)
                : item?.cash_given_amount != null && item?.cash_given_amount !== ''
                  ? Number(item.cash_given_amount)
                  : null,
    };
};

const FuelSettlementDetails = () => {
    const { setSubheading } = useOutletContext();
    const [searchParams] = useSearchParams();
    const fuelPumpId = searchParams.get('fuel_pump_id') || '';
    const fuelPumpName = searchParams.get('fuel_pump_name') || '-';
    const miplLocation = searchParams.get('mipl_location') || '-';

    useEffect(() => {
        setSubheading("View Fuel Amount Settlement Details");
        return () => {
            setSubheading("");
        };
    }, [setSubheading]);

    const [dates, setDates] = useState([null, null]);
    const [appliedRange, setAppliedRange] = useState([null, null]);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [rows, setRows] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [activeView, setActiveView] = useState('listing');
    const [logRows, setLogRows] = useState([]);
    const [logLoading, setLogLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [amountSettlement, setAmountSettlement] = useState(false);
    const [totalPayment, setTotalPayment] = useState(null);
    const [paymentMode, setPaymentMode] = useState('Cash');
    const [givenByName, setGivenByName] = useState('');
    const [settlementAmount, setSettlementAmount] = useState('');
    const [submitLoading, setSubmitLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [tdsAmount, setTdsAmount] = useState('');
    const [editSettlementOpen, setEditSettlementOpen] = useState(false);
    const [editingSettlementId, setEditingSettlementId] = useState('');
    const [editSettlementAmount, setEditSettlementAmount] = useState('');
    const [editTdsAmount, setEditTdsAmount] = useState('');
    const [editLoading, setEditLoading] = useState(false);
    const [editSettlementAmountError, setEditSettlementAmountError] = useState('');
    const resetSettlementForm = () => {
        setPaymentMode('Cash');
        setGivenByName('');
        setSettlementAmount('');
        setTdsAmount('');
    };

    const openSettlementModal = () => {
        resetSettlementForm();
        setAmountSettlement(true);
    };

    const closeSettlementModal = () => {
        setAmountSettlement(false);
        resetSettlementForm();
    };

    const openEditSettlementModal = (row) => {
        if (!row?.id) return;
        setEditingSettlementId(row.id);
        setEditSettlementAmount(
            row.settlementAmountRaw != null && !Number.isNaN(row.settlementAmountRaw)
                ? String(row.settlementAmountRaw)
                : ''
        );
        setEditTdsAmount(
            row.tdsAmountRaw != null && !Number.isNaN(row.tdsAmountRaw) && row.tdsAmountRaw !== 0
                ? String(row.tdsAmountRaw)
                : ''
        );
        setEditSettlementAmountError('');
        setEditSettlementOpen(true);
    };

    const closeEditSettlementModal = () => {
        setEditSettlementOpen(false);
        setEditingSettlementId('');
        setEditSettlementAmount('');
        setEditTdsAmount('');
        setEditSettlementAmountError('');
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    useEffect(() => {           
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    const handleApplyDates = () => {
        const [start, end] = dates;
        if (!start || !end) {
            notifyError('Please select both start and end dates');
            return false;
        }
        setAppliedRange([start, end]);
        setCurrentPage(1);
        return true;
    };

    const handleClearDates = () => {
        setDates([null, null]);
        setAppliedRange([null, null]);
        setCurrentPage(1);
    };

    const fetchSettlementDetails = useCallback(async () => {
        if (!fuelPumpId) {
            setRows([]);
            return;
        }

        const [from, to] = appliedRange;
        const fromDate = toApiDate(from);
        const toDate = toApiDate(to);

        setListLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(currentPage),
                limit: String(itemsPerPage),
                fuel_pump_id: fuelPumpId,
            });
            if (fromDate && toDate) {
                appendDateRangeParams(
                    params,
                    { start: fromDate, end: toDate },
                    DATE_PARAM_KEYS.fromTo
                );
            }
            const q = clampSearchInput(debouncedSearch).trim();

            if (q) params.set('search', q);

            const response = await handleApiCall(
                'get',
                `${endpoint.viewFuelSettlementDetails}?${params.toString()}`,
                {}
            );

            if (response?.data?.code === 200) {
                const d = response?.data?.data || {};
                const raw = Array.isArray(d.fuel_settlement_details)
                    ? d.fuel_settlement_details
                    : [];
                setRows(raw.map(mapSettlementDetailRow));
                setTotalRecords(d.total ?? raw.length);
                setTotalPages(Math.max(1, d.total_pages ?? 1));
                const balance = d.total_payment ?? d.total_balance ?? d.total_amount;
                setTotalPayment(
                    balance != null && balance !== '' && !Number.isNaN(Number(balance))
                        ? Number(balance)
                        : null
                );
            } else {
                const errMsg =
                    response?.response?.data?.errorMessage ||
                    response?.response?.data?.message ||
                    response?.data?.message ||
                    'Failed to load settlement details';
                notifyError(errMsg);
                setRows([]);
                setTotalRecords(0);
                setTotalPages(1);
            }
        } catch {
            notifyError('Failed to load settlement details');
            setRows([]);
            setTotalRecords(0);
            setTotalPages(1);
        } finally {
            setListLoading(false);
        }
    }, [fuelPumpId, appliedRange, currentPage, itemsPerPage, debouncedSearch]);

    const fetchFuelSettlementCsvLog = useCallback(async () => {
        if (!fuelPumpId) {
            setLogRows([]);
            return;
        }

        setLogLoading(true);
        try {
            const params = new URLSearchParams({ fuel_pump_id: fuelPumpId });
            const response = await handleApiCall(
                'get',
                `${endpoint.getFuelSettlementCsvLog}?${params.toString()}`,
                {}
            );

            if (response?.data?.code === 200) {
                const d = response?.data?.data || {};
                const list = extractCsvLogList(d);
                setLogRows(list.map(mapFuelSettlementCsvLogRow));
            } else {
                setLogRows([]);
                const errMsg =
                    response?.response?.data?.errorMessage ||
                    response?.response?.data?.message ||
                    response?.data?.message ||
                    'Failed to load CSV log';
                notifyError(errMsg);
            }
        } catch {
            setLogRows([]);
            notifyError('Failed to load CSV log');
        } finally {
            setLogLoading(false);
        }
    }, [fuelPumpId]);

    useEffect(() => {
        if (activeView !== 'listing') return;
        fetchSettlementDetails();
    }, [activeView, fetchSettlementDetails]);

    useEffect(() => {
        if (activeView !== 'log') return;
        fetchFuelSettlementCsvLog();
    }, [activeView, fetchFuelSettlementCsvLog]);

    const handleCsvReady = useCallback(
        (payload) => {
            if (!isFuelSettlementCsvType(payload?.type)) return;

            const logId = payload?.csv_log_id;
            setLogRows((prev) => {
                const idx = logId ? prev.findIndex((row) => row.id === logId) : -1;
                if (idx >= 0) {
                    const next = [...prev];
                    next[idx] = patchSalaryCsvLogRow(prev[idx], payload);
                    return next;
                }
                return prev;
            });
            fetchFuelSettlementCsvLog();
        },
        [fetchFuelSettlementCsvLog]
    );

    useAdminCsvReady(handleCsvReady);

    const handleExportCsv = async () => {
        if (!fuelPumpId) {
            notifyError('Fuel pump not found');
            return;
        }
        const [from, to] = appliedRange;
        const fromDate = toApiDate(from);
        const toDate = toApiDate(to);
        // if (!fromDate || !toDate) {
        //     notifyError('Please select a date range before exporting');
        //     return;
        // }

        setExportLoading(true);
        try {
            const params = new URLSearchParams({ fuel_pump_id: fuelPumpId });
            appendDateRangeParams(
                params,
                { start: fromDate, end: toDate },
                DATE_PARAM_KEYS.fromTo
            );
            const response = await handleApiCall(
                'get',
                `${endpoint.exportFuelSettlementCsv}?${params.toString()}`,
                {}
            );

            if (response?.data?.code === 200) {
                const raw = Array.isArray(response?.data?.data) ? response.data.data : [];
                if (raw.length > 0) {
                    const csvRows = raw.map(mapSettlementDetailRow);
                    const safeName = (fuelPumpName || 'fuel-settlement')
                        .replace(/[^\w\s-]/g, '')
                        .trim()
                        .replace(/\s+/g, '-')
                        .slice(0, 40) || 'fuel-settlement';
                    downloadCsvFile(csvRows, `${safeName}_${fromDate || 'all'}_${toDate || 'all'}.csv`);
                }
                await fetchFuelSettlementCsvLog();
                setActiveView('log');
                return;
            }

            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message ||
                'Failed to export CSV';
            notifyError(errMsg);
        } catch {
            notifyError('Failed to export CSV');
        } finally {
            setExportLoading(false);
        }
    };

    const handleSubmitSettlement = async () => {
        if (!fuelPumpId) {
            notifyError('Fuel pump not found');
            return;
        }
        if (totalPayment == null || Number.isNaN(Number(totalPayment))) {
            notifyError('Total balance is not available');
            return;
        }
        if (!paymentMode) {
            notifyError('Please select payment mode');
            return;
        }
        const givenBy = givenByName.trim();
        if (!givenBy) {
            notifyError('Please enter cash given by');
            return;
        }
        const settled = Number(String(settlementAmount).replace(/,/g, '').trim());
        if (!settlementAmount || Number.isNaN(settled) || settled <= 0) {
            notifyError('Please enter a valid settled amount');
            return;
        }
        const trimmedTds = String(tdsAmount ?? '').replace(/,/g, '').trim();

        setSubmitLoading(true);
        try {
            const payload = {
                fuel_pump_id: fuelPumpId,
                total_payment: Number(totalPayment),
                payment_mode_type: paymentMode,
                given_by_name: givenBy,
                settlement_amount: settled,
            };
            if (trimmedTds !== '') {
                payload.tds_amount = Number(trimmedTds);
            }

            const response = await handleApiCall(
                'post',
                endpoint.submitFuelSettlement,
                payload
            );

            if (response?.data?.code === 200) {
                notifySuccess(response?.data?.message || 'Fuel settlement submitted successfully');
                closeSettlementModal();
                fetchSettlementDetails();
                return;
            }

            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message ||
                'Failed to submit fuel settlement';
            notifyError(errMsg);
        } catch {
            notifyError('Failed to submit fuel settlement');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleEditSettlement = async () => {
        if (!editingSettlementId) {
            notifyError('Settlement not found');
            return;
        }

        const trimmedAmount = String(editSettlementAmount).replace(/,/g, '').trim();
        const amount = Number(trimmedAmount);
        const trimmedTds = String(editTdsAmount ?? '').replace(/,/g, '').trim();

        if (!trimmedAmount) {
            setEditSettlementAmountError('Please enter settlement amount');
            return;
        }
        if (Number.isNaN(amount) || amount <= 0) {
            setEditSettlementAmountError('Please enter a valid settlement amount');
            return;
        }

        setEditSettlementAmountError('');

        setEditLoading(true);
        try {
            const payload = {
                settlement_id: editingSettlementId,
                settlement_amount: amount,
            };
            if (trimmedTds !== '') {
                payload.tds_amount = Number(trimmedTds);
            }

            const response = await handleApiCall(
                'put',
                endpoint.editFuelSettlement,
                payload
            );

            if (response?.data?.code === 200) {
                notifySuccess(response?.data?.message || 'Settlement updated successfully');
                closeEditSettlementModal();
                fetchSettlementDetails();
                return;
            }

            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message ||
                'Failed to update settlement';
            notifyError(errMsg);
        } catch {
            notifyError('Failed to update settlement');
        } finally {
            setEditLoading(false);
        }
    };

    const handleDownloadLogFile = (url) => {
        if (!url) {
            notifyError('Download link not available');
            return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <>
        <Toastify/>
     <Link to="/fuel-pump-management" className="btn btn-primary ml-auto mt-3">
            Back
          </Link>

            <div className="tab-pane-content md:mt-7">
                <center className='font-UbuntuRegular'>
                    <h3 className='font-UbuntuMedium mdtext-xl'>{fuelPumpName}</h3>
                    <div className='max-[767px]:text-sm'>{miplLocation}</div>
                </center>

                {!fuelPumpId && (
                    <p className="text-center text-red-500 mb-4">
                        Fuel pump not found.{' '}
                        <Link to="/fuel-pump-management" className="underline">
                            Go back to Fuel Pump Management
                        </Link>
                    </p>
                )}

                <div className="mb-1 md:mb-4 flex flex-wrap gap-2 max-[767px]:justify-center mt-2 md:mt-0 max-[767px]:[&_button]:rounded-full max-[767px]:[&_button]:text-xs">
                    <ViewTabButton
                        active={activeView === 'listing'}
                        label="Listing"
                        onClick={() => setActiveView('listing')}
                    />
                    <ViewTabButton
                        active={activeView === 'log'}
                        label="Log"
                        onClick={() => setActiveView('log')}
                    />
                </div>

                {activeView === 'listing' && (
                <>
                <div className='flex flex-wrap gap-x-2.5 gap-y-2 md:mb-4 mb-2.5 items-center'>
                    <input
                        type="text"
                        placeholder="Search..."
                        className="border px-3 py-2 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm max-[767px]:w-[calc(100%-62px)]"
                        value={search}
                        maxLength={SEARCH_MAX_LENGTH}

                        onChange={(e) => setSearch(clampSearchInput(e.target.value))}
                    />
                    <div className='max-[767px]:[&_span.inline-flex]:hidden max-[767px]:[&_.absolute]:left-auto max-[767px]:[&_.absolute]:right-0'>
                        <DateRangeFilter
                            variant="popover"
                            range={dates}
                            onRangeChange={setDates}
                            onApply={handleApplyDates}
                            applyDisabled={!dates[0] || !dates[1]}
                            onClear={handleClearDates}
                            appliedFrom={appliedRange[0] ? toApiDate(appliedRange[0]) : ''}
                            appliedTo={appliedRange[1] ? toApiDate(appliedRange[1]) : ''}
                            showAppliedLabel
                            appliedLabelPrefix="Date range: "
                        />
                    </div>
                    <PermissionGate module={MODULES.FUEL_PUMP} action="export">
                      <button
                          type="button"
                          className="btn btn-danger ml-auto"
                          onClick={handleExportCsv}
                          disabled={exportLoading || !fuelPumpId}
                      >
                          {exportLoading ? 'Exporting…' : 'Export CSV'}
                      </button>
                    </PermissionGate>
                    <PermissionGate module={MODULES.FUEL_PUMP} action="add">
                      <button
                          type="button"
                          className="btn btn-danger"
                          onClick={openSettlementModal}
                      >
                          Fuel Amount Settlement
                      </button>
                    </PermissionGate>
                </div>

                <div className="common-table-wrapper m-0">
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Date</th>
                                    <th>Vehicle Number</th>
                                    <th>Fuel Quantity</th>
                                    <th>Fuel Price</th>
                                    <th>Fuel Operator Name</th>
                                    <th>Fuel Amount</th>
                                    <th>Swap Amount By Card</th>
                                    <th>Cash Given By</th>
                                    <th>Cash</th>
                                    <th>TDS</th>
                                    <th>Remaining Balance</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listLoading ? (
                                    <tr>
                                        <td colSpan={13} className="text-center py-6 text-gray-500">
                                            Loading…
                                        </td>
                                    </tr>
                                ) : rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={13} className="text-center py-6 text-gray-500">
                                            No settlement records found
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row, index) => (
                                        <tr key={row.id || `row-${index}`}>
                                            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                            <td>{row.date}</td>
                                            <td>{row.vehicleNumber}</td>
                                            <td>{row.fuelQuantity}</td>
                                            <td>{row.fuelPrice}</td>
                                            <td>{row.fuelOperatorName}</td>
                                            <td>{row.fuelAmount}</td>
                                            <td>{row.swapAmountByCard}</td>
                                            <td>{row.cashGivenBy}</td>
                                            <td>{row.cash}</td>
                                            <td>{row.tds}</td>
                                            <td>{row.remainingBalance}</td>
                                            <td>
                                                {row.id && !row.vehicleId ? (
                                                    <PermissionGate module={MODULES.FUEL_PUMP} action="edit">
                                                      <button
                                                          type="button"
                                                          className="p-0 border-0 bg-transparent cursor-pointer"
                                                          onClick={() => openEditSettlementModal(row)}
                                                          aria-label="Edit settlement amount"
                                                      >
                                                          <img
                                                              src={EditIcon}
                                                              alt="Edit"
                                                              className="h-7 min-w-[22px]"
                                                          />
                                                      </button>
                                                    </PermissionGate>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="flex gap-x-4 items-center mt-4">
                    <select
                        className='p-2 shadow-sm outline-none max-[767px]:text-xs'
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                    <div className="text-xs md:text-sm text-gray-500 ml-auto">
                        {totalRecords} records found
                    </div>
                    <div className="flex items-center gap-2 md:pr-8">
                        <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1 || listLoading}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage === 1 ? '' : 'cursor-pointer'}`}
                        >
                            <img src={LeftAngle} className='invert grayscale brightness-100' alt="Previous" />
                        </button>
                        <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                        <span className="text-sm text-gray-600 text-nowrap">
                            of {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages || listLoading}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage === totalPages ? '' : 'cursor-pointer'}`}
                        >
                            <img src={RightAngle} className='invert grayscale brightness-100' alt="Next" />
                        </button>
                    </div>
                </div>
                </>
                )}

                {activeView === 'log' && (
                <>
                <div className="common-table-wrapper m-0">
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>File Name</th>
                                    <th>Filters</th>
                                    <th>Created Date</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logLoading ? (
                                    <tr>
                                        <td colSpan={6} className="text-center py-6 text-gray-500">
                                            Loading…
                                        </td>
                                    </tr>
                                ) : logRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center py-6 text-gray-500">
                                            No CSV exports found
                                        </td>
                                    </tr>
                                ) : (
                                    logRows.map((row, index) => (
                                        <tr key={row.id || `log-${index}`}>
                                            <td>{index + 1}</td>
                                            <td className="max-w-[240px] truncate" title={row.fileName}>
                                                {row.fileName}
                                            </td>
                                            <td className="max-w-[280px] whitespace-normal" title={row.filters}>
                                                {row.filters}
                                            </td>
                                            <td className="whitespace-nowrap">{row.createdAt}</td>
                                            <td>
                                                <CsvStatusBadge status={row.status} />
                                            </td>
                                            <td>
                                                {row.status === 'Complete' && row.downloadUrl ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownloadLogFile(row.downloadUrl)}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border-0 !bg-primary"
                                                        aria-label={`Download ${row.fileName}`}
                                                    >
                                                        <img src={DownloadIcon} alt="" className="h-3.5 w-3.5" />
                                                    </button>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="mt-4 flex justify-end text-sm text-gray-500">
                    {logRows.length} record{logRows.length === 1 ? '' : 's'} found
                </div>
                </>
                )}

            </div>

            {amountSettlement && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999]">
                    <div className="bg-white p-6 rounded-xl max-w-96 w-full [&_.form-control]:shadow-none">
                        <p className="mb-4 text-center font-bold">Fuel Amount Settlement</p>
                        <form
                            className='flex flex-col gap-y-4 [&_label]:mb-1.5 font-UbuntuRegular [&_label]:font-UbuntuMedium [&_.form-control]:h-11'
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSubmitSettlement();
                            }}
                        >
                            <div className='form-group'>
                                <label>Total Balance</label>
                                <input
                                    type="text"
                                    disabled
                                    value={totalPayment != null ? formatMoney(totalPayment) : '-'}
                                    className='form-control'
                                />
                            </div>
                            <div className='form-group'>
                                <label>Payment Mode</label>
                                <select
                                    className='form-control dropdown'
                                    value={paymentMode}
                                    onChange={(e) => setPaymentMode(e.target.value)}
                                >
                                    {/* <option value="">Select Payment Mode</option> */}
                                    <option value="Card">Card</option>
                                    <option value="Cash">Cash</option>
                                </select>
                            </div>
                            <div className='form-group'>
                                <label> {paymentMode === 'Card' ? 'Card Given By' : 'Cash Given By'}</label>
                                <input
                                    type="text"
                                    value={givenByName}
                                    onChange={(e) => setGivenByName(e.target.value.replace(/\d/g, ''))}
                                    className='form-control'
                                    placeholder="Enter name"
                                />
                            </div>
                            <div className='form-group'>
                                <label>Settled Amount</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={settlementAmount}
                                    onChange={(e) => setSettlementAmount(e.target.value)}
                                    className='form-control'
                                    placeholder="Enter amount"
                                />
                            </div>
                            <div className='form-group'>
                                <label>TDS Amount</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={tdsAmount}
                                    onChange={(e) => setTdsAmount(e.target.value)}
                                    className='form-control'
                                    placeholder="Enter amount"
                                />
                            </div>
                            <div className="flex gap-3 justify-center mt-2">
                                <button
                                    type="button"
                                    onClick={closeSettlementModal}
                                    className="btn btn-primary"
                                    disabled={submitLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-danger"
                                    disabled={submitLoading}
                                >
                                    {submitLoading ? 'Submitting…' : 'Submit'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {editSettlementOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999]">
                    <div className="bg-white p-6 rounded-xl max-w-96 w-full [&_.form-control]:shadow-none">
                        <p className="mb-4 text-center font-bold">Edit Settlement Amount</p>
                        <form
                            className="flex flex-col gap-y-4 [&_label]:mb-1.5 font-UbuntuRegular [&_label]:font-UbuntuMedium [&_.form-control]:h-11"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleEditSettlement();
                            }}
                        >
                            <div className="form-group">
                                <label>Settlement Amount</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={editSettlementAmount}
                                    onChange={(e) => {
                                        setEditSettlementAmount(e.target.value);
                                        if (editSettlementAmountError) {
                                            setEditSettlementAmountError('');
                                        }
                                    }}
                                    className={`form-control${
                                        editSettlementAmountError ? ' border-red-500' : ''
                                    }`}
                                    placeholder="Enter settlement amount"
                                    aria-invalid={Boolean(editSettlementAmountError)}
                                    aria-describedby={
                                        editSettlementAmountError
                                            ? 'edit-settlement-amount-error'
                                            : undefined
                                    }
                                />
                                {editSettlementAmountError ? (
                                    <p
                                        id="edit-settlement-amount-error"
                                        className="mb-0 mt-1 text-sm text-red-600"
                                        role="alert"
                                    >
                                        {editSettlementAmountError}
                                    </p>
                                ) : null}
                            </div>
                            <div className="form-group">
                                <label>TDS Amount</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={editTdsAmount}
                                    onChange={(e) => setEditTdsAmount(e.target.value)}
                                    className="form-control"
                                    placeholder="Enter TDS amount"
                                />
                            </div>
                            <div className="flex gap-3 justify-center mt-2">
                                <button
                                    type="button"
                                    onClick={closeEditSettlementModal}
                                    className="btn btn-primary"
                                    disabled={editLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-danger"
                                    disabled={editLoading}
                                >
                                    {editLoading ? 'Updating…' : 'Update'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default FuelSettlementDetails;
